import { useCatalogContentStore } from '@/stores/catalogContent'
import { useMainStore } from '@/stores/main'
import { defineStore } from 'pinia'

import { orderBy, uniqBy } from 'lodash-es'

import {
  isApplicableToProofType,
  isCatalogBundleApplicableToProofType
} from '@/lib/catalogApplicability'
import { bundleHasSheetSelectionGroup } from '@/lib/packageBuilder'
import { withPriceRuleEndDate } from '@/lib/priceRules'
import { stampRewardTierStates } from '@/lib/rewardLadder'
import { getAvailableNotificationForDisplay, sortBundlesByZOrder } from '@/lib/utils'

import { ActiveUpsell } from '@/types/ActiveUpsell'
import { CadyMarket } from '@/types/CadyMarket'
import type { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'
import type { CatalogFlowStep } from '@/types/CatalogFlowStep'
import { ContentfulBundleDisplayMethod } from '@/types/ContentfulBundleDisplayMethod'
import type { FreeGiftContent } from '@/types/FreeGiftContent'
import { type RewardLadderTier, type RewardTier, RewardTierState } from '@/types/RewardTier'
import type { RewardTrackerContent } from '@/types/RewardTrackerContent'

import {
  BundleDisplayContext,
  CatalogBundleDisplayMode,
  CatalogFlowBundleType
} from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import {
  ActivePriceRuleStorefrontDto,
  CatalogBundleStorefrontDto,
  CatalogPromotionStorefrontDto,
  CatalogStorefrontDto,
  RewardPromotionStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import { CatalogFlowStepBundleStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import type { ContentfulBundleFlat } from '#contentful/ContentfulBundle'
import { ContentfulBundleFilterTag } from '#contentful/ContentfulBundleFilterTag'
import type { ContentfulCatalogFlowStepFlat } from '#contentful/ContentfulCatalogFlowStep'
import type { ContentfulCustomizationPromptFlat } from '#contentful/ContentfulCustomizationPrompt'
import type { ContentfulEntry, FlatContentfulEntry } from '#contentful/ContentfulEntrySys'
import type { ContentfulItemContentFlat } from '#contentful/ContentfulItemContent'
import { ContentfulNotification } from '#contentful/ContentfulNotification'
import type { ContentfulPriceRuleFlat } from '#contentful/ContentfulPriceRule'
import type { ContentfulPriceRuleSprite } from '#contentful/ContentfulPriceRuleSprite'
import type { ContentfulPromotion } from '#contentful/ContentfulPromotion'

// Re-export for backward compatibility
export type { ActiveUpsell }
// @deprecated Use ActiveUpsell instead
export type ActiveUpsellForFlowStep = ActiveUpsell

interface CatalogStoreState {
  catalogData: CatalogStorefrontDto | null
  dismissedNotifications: string[]
  notificationsContent: ContentfulEntry<ContentfulNotification>[] | null | undefined
  activeUpsellsDismissed: string[]
}

export const useCatalogStore = defineStore('catalog', {
  state: (): CatalogStoreState => ({
    // All state properties should be added to the manual resetStore action method below
    catalogData: null,
    dismissedNotifications: [],
    notificationsContent: null,
    activeUpsellsDismissed: []
  }),
  getters: {
    /** The Contentful catalog payload, held by `useCatalogContentStore`; exposed here so existing readers need no change. */
    catalogContent(): any {
      return useCatalogContentStore().catalogContent
    },
    catalogBundlesWithContent(state): CatalogBundleStorefrontDtoWithContent[] {
      const mainStore = useMainStore()
      const isSenior = mainStore.segmentationKey.market === CadyMarket.Senior
      const subjectProofType = mainStore.segmentationKey.proofType

      const result: CatalogBundleStorefrontDtoWithContent[] = []

      state.catalogData?.catalogBundles?.forEach((cb) => {
        const bundleContent = this.catalogContent?.bundles.find(
          (b: ContentfulBundleFlat) => b.sunny16Guid === cb.bundleId
        )
        if (!bundleContent) return

        if (isSenior && !isCatalogBundleApplicableToProofType(cb, subjectProofType)) return

        result.push({ ...cb, content: bundleContent })
      })

      return result
    },
    /**
     * The Senior Preorder offer cards: content-joined, proof-type-scoped catalog bundles whose
     * display context is `Always`, minus the retouching bundle (carried in the catalog only to feed
     * the post-Continue drawer).
     *
     * `Always` only — no customer-service `BackofficeOnly` extension like `CatalogFlowStep`'s
     * `listableBundles`. The offer page is a marketing-link entry surface, so anything scoped to
     * upsell, backoffice, disabled or system contexts is never an offer card.
     *
     * Single source of truth so `PreorderView`'s listing and the data loader's render/fallback gate
     * ask the SAME question. They previously diverged — the gate tested raw `catalogData.catalogBundles`
     * while the page rendered this filtered set, so a catalog whose bundles have no published
     * Contentful entry (or only carries retouching, or is fully proof-filtered) passed the gate and
     * then rendered an empty page instead of falling back.
     */
    preorderOfferBundles(): CatalogBundleStorefrontDtoWithContent[] {
      const retouchingBundleId = this.retouchingBundleId
      return this.catalogBundlesWithContent.filter(
        (cb) =>
          cb.bundleId !== retouchingBundleId &&
          cb.bundle?.displayOptionsDefault?.displayContext === BundleDisplayContext.Always
      )
    },
    /**
     * The catalog bundles Sunny16 auto-adds to the cart on its first `/prepare` (they arrive as cart
     * bundles carrying `Source=AutomaticUpsell`). Checkout's in-cart upsell container draws its rows
     * from this set rather than from the cart, so an upsell the customer unchecked can still be
     * shown, unchecked.
     *
     * Read from `catalogData.automaticUpsell`, the projection of the catalog-level `AutomaticUpsell`
     * attribute that `/prepare` itself acts on, so the storefront offers exactly what Sunny16 would
     * add. Resolved through `catalogBundlesWithContent` so a bundle with no Contentful entry, or one
     * outside the subject's proof type, is not offered. At most one today; an array so the container
     * has one shape whatever the catalog configures. The suppressor check (`suppressorItemIds`)
     * needs the cart and lives in `commerceStore.cartItemsDetails`.
     */
    inCartUpsellCatalogBundles(state: CatalogStoreState): CatalogBundleStorefrontDtoWithContent[] {
      const upsellCatalogBundleId = state.catalogData?.automaticUpsell?.upsellCatalogBundleId
      if (!upsellCatalogBundleId) return []

      return this.catalogBundlesWithContent.filter(
        (cb) => cb.catalogBundleId === upsellCatalogBundleId
      )
    },
    /**
     * Item ids that stop Sunny16 from auto-adding the in-cart upsell when any of them is in the cart
     * (`CadyAutomaticUpsell.SuppressorItemIds`). Empty when the catalog configures no upsell.
     */
    inCartUpsellSuppressorItemIds(state: CatalogStoreState): string[] {
      return state.catalogData?.automaticUpsell?.suppressorItemIds ?? []
    },
    /**
     * Cross-sell pool for the cart-flyout Quick Adds (YMAL): catalog bundles flagged
     * `displayMode === CrossSell`. Proof-type scoping is already applied by `catalogBundlesWithContent`
     * (Senior). The flyout selection (drop in-cart, gap-aware ordering, cap N) lives in `useQuickAdds`.
     */
    crossSellCatalogBundles(): CatalogBundleStorefrontDtoWithContent[] {
      return this.catalogBundlesWithContent.filter(
        (cb) => cb.displayMode === CatalogBundleDisplayMode.CrossSell
      )
    },
    /**
     * Whether the catalog has flow steps the storefront can actually render.
     *
     * Counts `catalogFlowStepsCombined` rather than `catalogData.catalogFlowSteps`:
     * the combined getter drops any step whose Contentful content is missing, so the
     * raw count can be non-zero while nothing is renderable. Consumers use this to
     * decide whether to route into the flow at all, and routing into a flow with no
     * renderable steps leaves `selectedCatalogFlowStep` undefined.
     */
    catalogHasFlowSteps(): boolean {
      return this.catalogFlowStepsCombined.length > 0
    },
    catalogFlowStepsCombined(state: CatalogStoreState): CatalogFlowStep[] {
      const result: CatalogFlowStep[] = []

      // foreach flow step, get its content
      orderBy(state.catalogData?.catalogFlowSteps, 'zOrder').forEach((cfs) => {
        const flowStepContent = this.catalogContent?.catalogFlowSteps.find(
          (cfsc: ContentfulCatalogFlowStepFlat) => cfsc.sunny16Guid === cfs.catalogFlowStepId
        )

        if (!flowStepContent) {
          // if there is no content for this flow step, skip it. it will break the storefront app if we try to display it.
          return
        }

        const flowStepBundlesIds = cfs.catalogFlowStepBundles
          .filter(
            (cfsb: CatalogFlowStepBundleStorefrontDto) =>
              cfsb.type === CatalogFlowBundleType.Standard
          )
          .map((fsb: CatalogFlowStepBundleStorefrontDto) => fsb.catalogBundleId)

        const flowStepUpsellBundleIds = cfs.catalogFlowStepBundles
          .filter(
            (cfsb: CatalogFlowStepBundleStorefrontDto) => cfsb.type === CatalogFlowBundleType.Upsell
          )
          .map((fsb: CatalogFlowStepBundleStorefrontDto) => fsb.catalogBundleId)

        const bundles = sortBundlesByZOrder(
          this.catalogBundlesWithContent.filter((cb: CatalogBundleStorefrontDtoWithContent) =>
            flowStepBundlesIds.includes(cb.catalogBundleId)
          ),
          cfs.catalogFlowStepBundles,
          CatalogFlowBundleType.Standard
        )

        const upsellBundles = sortBundlesByZOrder(
          this.catalogBundlesWithContent.filter((cb) =>
            flowStepUpsellBundleIds.includes(cb.catalogBundleId)
          ) as CatalogBundleStorefrontDtoWithContent[],
          cfs.catalogFlowStepBundles,
          CatalogFlowBundleType.Upsell
        )

        // get all available bundle filters from the bundles and upsell bundles
        const availableBundleFiltersAll = [
          ...bundles
            .filter((b) => (b.content.filterTags?.length ?? 0) > 0)
            .flatMap((b) => b.content.filterTags),
          ...upsellBundles
            .filter((b) => (b.content.filterTags?.length ?? 0) > 0)
            .flatMap((b) => b.content.filterTags)
        ]

        result.push({
          flowStepData: cfs,
          flowStepContent,
          // Use the current result length so numbering is sequential for included steps only
          number: result.length + 1,
          bundles,
          upsellBundles,
          bundleFilters: uniqBy(
            availableBundleFiltersAll,
            'id'
          ) as FlatContentfulEntry<ContentfulBundleFilterTag>[]
        })
      })

      return result
    },
    /**
     * The 1-based `number` (within `catalogFlowStepsCombined`) of the catalog
     * flow step that offers Package Builder (SheetSelection) bundles, or null
     * when none. The dedicated Package Builder commerce step is positioned
     * immediately after this step.
     *
     * Anchoring by content — not a hardcoded index — mirrors how the retouching
     * step keys off `retouchingBundleId`. Per CADY catalog convention all
     * builder bundles live in one flow step; if that ever loosens we anchor
     * after the *last* builder-bearing step so the cart is fully assembled
     * before configuration. Returns null when the feature flag is off
     * (`bundleHasSheetSelectionGroup` short-circuits on it).
     */
    packageBuilderAnchorFlowStepNumber(): number | null {
      const anchored = this.catalogFlowStepsCombined.filter((fs) =>
        fs.bundles.some((b) => bundleHasSheetSelectionGroup(b.bundle))
      )
      return anchored.length ? anchored[anchored.length - 1].number : null
    },
    catalogBundleContent(): (bundleId: string) => ContentfulBundleFlat | undefined {
      return (bundleId: string) => {
        return this.catalogContent?.bundles?.find(
          (cb: ContentfulBundleFlat) => cb.sunny16Guid === bundleId
        )
      }
    },
    catalogItemContent(): (itemId: string) => ContentfulItemContentFlat | undefined {
      return (itemId: string) => {
        return this.catalogContent?.catalogItemContents?.find((ci) => ci.sunny16Guid === itemId)
      }
    },
    customizationPromptContent(): (
      promptId: string
    ) => ContentfulCustomizationPromptFlat | undefined {
      return (promptId: string) => {
        return this.catalogContent?.customizationPrompts?.find(
          (cp: ContentfulCustomizationPromptFlat) => cp.sunny16Guid === promptId
        )
      }
    },
    promotionContent(): (promotionId: string) => ContentfulPromotion | undefined {
      return (promotionId: string) => {
        return this.catalogContent?.promotions?.find(
          (p: ContentfulPromotion) => p.sunny16Guid === promotionId
        )
      }
    },
    catalogHasRetouchingBundle(): boolean {
      return this.retouchingBundleId !== null
    },
    retouchingBundle(state: CatalogStoreState): CatalogBundleStorefrontDto | null {
      return (
        state.catalogData?.catalogBundles?.find((b) => b.bundleId === this.retouchingBundleId) ||
        null
      )
    },
    retouchingBundleWithContent(): CatalogBundleStorefrontDtoWithContent | null {
      if (this.retouchingBundleId) {
        return (
          this.catalogBundlesWithContent.find(
            (cbwc) => cbwc.bundleId === this.retouchingBundleId
          ) || null
        )
      } else {
        return null
      }
    },
    retouchingBundleId(): string | null {
      const retouchingBundleContent = this.catalogContent?.bundles.find(
        (b: ContentfulBundleFlat) => b.displayMethod === ContentfulBundleDisplayMethod.Retouching
      )

      if (retouchingBundleContent) {
        return retouchingBundleContent.sunny16Guid
      }

      return null
    },
    poseBackgroundSelectionBundleId(): string | null {
      const poseBackgroundSelectionBundleContent = this.catalogContent?.bundles.find(
        (b: ContentfulBundleFlat) =>
          b.displayMethod === ContentfulBundleDisplayMethod.PoseBackgroundSelection
      )

      if (poseBackgroundSelectionBundleContent) {
        return poseBackgroundSelectionBundleContent.sunny16Guid.toLowerCase()
      }

      return null
    },
    /**
     * Whether a `bundleId` is the pose/background-selection bundle’s.
     *
     * Case-insensitive, for the reason `isPoseBackgroundSelectionFlowStep` gives: this getter
     * lower-cases its `sunny16Guid` while `bundleId`s from the catalog and the cart are passed
     * through untouched.
     */
    isPoseBackgroundSelectionBundleId(): (bundleId: string | undefined) => boolean {
      return (bundleId: string | undefined): boolean => {
        const poseBackgroundSelectionBundleId = this.poseBackgroundSelectionBundleId

        return (
          !!poseBackgroundSelectionBundleId &&
          bundleId?.toLowerCase() === poseBackgroundSelectionBundleId
        )
      }
    },
    /**
     * Whether the `catalog/flow/:flowStep` route param refers to the pose/background-selection step.
     *
     * That step has no route of its own — it shares `catalog-flow-step` with every other step — so it can
     * only be identified by the bundle it displays. Both halves of that live here rather than in the
     * caller: the param is 1-based into `catalogFlowStepsCombined` (the same mapping `CatalogFlowLayout`
     * uses to pick the step it renders), and the id comparison is case-insensitive because
     * `poseBackgroundSelectionBundleId` lower-cases its `sunny16Guid` while catalog `bundleId`s are passed
     * through untouched.
     *
     * Returns `false` for a missing or unparseable param, so a caller can hand the raw route value over.
     */
    isPoseBackgroundSelectionFlowStep(): (flowStepParam: string | undefined) => boolean {
      return (flowStepParam: string | undefined): boolean => {
        const bundleId = this.poseBackgroundSelectionBundleId

        if (!bundleId) {
          return false
        }

        const flowStep = Number.parseInt(flowStepParam ?? '', 10)

        if (Number.isNaN(flowStep)) {
          return false
        }

        return !!this.catalogFlowStepsCombined[flowStep - 1]?.bundles.some(
          (bundle) => bundle.bundleId?.toLowerCase() === bundleId
        )
      }
    },
    nextRewardPromotion(): (cartSubTotal: number) => RewardPromotionStorefrontDto | null {
      return (cartSubTotal: number): RewardPromotionStorefrontDto | null => {
        const nextRewardPromotion =
          this.applicableRewardPromotions.find(
            (p: RewardPromotionStorefrontDto) => p.minimumSubtotal > cartSubTotal
          ) ?? null

        if (nextRewardPromotion) {
          return nextRewardPromotion
        }

        /* CADY requested to not show the progress UI if there are no more promotions to unlock, so this code is comment out
          // The below code will return the last reward promotion if the cart subtotal is greater than or equal to the last promotion's minimumSubtotal.
          // get the reward promotion with the highest minimumSubtotal
          const lastRewardPromotion = last(sortedPromotions)

          // if the cart subtotal is greater than or equal to the last reward promotion's minimumSubtotal, return it as the next reward promotion
          // this should display the rewards progress bar as "completed"
          if (lastRewardPromotion && cartSubTotal >= lastRewardPromotion.promotion.minimumSubtotal) {
            return lastRewardPromotion
          }
       */

        return null
      }
    },
    /**
     * The reward promotions that apply to the subject, threshold-sorted. The one definition of
     * reward applicability, read by both the tracker's {@link rewardLadder} and the legacy
     * {@link nextRewardPromotion}.
     *
     * Proof-type applicability is gated on BOTH the promotion's own `attributes` AND, when the reward
     * grants a bundle, that granted bundle's `attributes` (`additionalBundleId` matches a
     * `CatalogBundle.bundleId`). Either gate can withhold a tier; a missing/absent granted bundle is
     * treated as universal so a payload gap never hides everything.
     */
    applicableRewardPromotions(state: CatalogStoreState): RewardPromotionStorefrontDto[] {
      const subjectProofType = useMainStore().segmentationKey.proofType
      const catalogBundles = state.catalogData?.catalogBundles ?? []

      const applicable = (state.catalogData?.rewardPromotions ?? []).filter((p) => {
        // Promotion-level proof-type gate.
        if (!isApplicableToProofType(p.attributes, subjectProofType)) return false

        // Granted-bundle proof-type gate — only when the reward grants a bundle that's in the
        // payload. `additionalBundleId` matches `CatalogBundle.bundleId` (not `catalogBundleId`).
        if (p.additionalBundleId) {
          const grantedBundle = catalogBundles.find((cb) => cb.bundleId === p.additionalBundleId)
          if (
            grantedBundle &&
            !isApplicableToProofType(grantedBundle.attributes, subjectProofType)
          ) {
            return false
          }
        }

        return true
      })

      return orderBy(applicable, 'minimumSubtotal')
    },
    /**
     * Subtotal-independent reward ladder: the {@link applicableRewardPromotions}, with their
     * Contentful content resolved into presentation fields. This is the expensive part of the reward
     * model (filter + sort + per-tier content lookup) and is a true cached getter — it recomputes only
     * when the catalog payload, content, or subject proof type changes, NOT on every cart-subtotal
     * change. `rewardTiers(subtotal)` stamps the per-subtotal `state`/`amountToUnlock` onto these
     * entries.
     */
    rewardLadder(): RewardLadderTier[] {
      return this.applicableRewardPromotions.map((p): RewardLadderTier => {
        // Single content lookup per tier. Expanded tracker content (rewardTracker* fields), when
        // authored, takes precedence over legacy `retailName` and carries the unlocked label + icon.
        const content = this.promotionContent(p.promotionId)
        const trackerLabel = content?.rewardTrackerLabel?.trim() || undefined

        return {
          promotionId: p.promotionId,
          type: p.type,
          additionalBundleId: p.additionalBundleId ?? undefined,
          minimumSubtotal: p.minimumSubtotal,
          label: trackerLabel ?? content?.retailName ?? p.nameInternal,
          unlockedLabel: trackerLabel
            ? content?.rewardTrackerUnlockedLabel?.trim() || trackerLabel
            : undefined,
          iconUrl: trackerLabel ? content?.rewardTrackerIcon?.file?.url : undefined
        }
      })
    },
    /**
     * Full multi-tier reward model for a given cart subtotal: the cached {@link rewardLadder} stamped
     * with locked/current/unlocked state. The single `Current` tier is the next one to unlock. Drives
     * the RewardsTracker. Cheap per call — an O(n) stamping pass over the cached ladder.
     */
    rewardTiers(): (effectiveSubtotal: number) => RewardTier[] {
      return (effectiveSubtotal: number): RewardTier[] =>
        // The stamping rule itself lives in `lib/rewardLadder.ts` so the debug harness can apply it to
        // a synthetic ladder without reimplementing it — see `stampRewardTierStates`.
        stampRewardTierStates(this.rewardLadder, effectiveSubtotal)
    },
    /**
     * The single next reward tier the customer is working toward (`Current`), or `null` once all
     * applicable tiers are unlocked.
     */
    nextRewardTier(): (effectiveSubtotal: number) => RewardTier | null {
      return (effectiveSubtotal: number): RewardTier | null =>
        this.rewardTiers(effectiveSubtotal).find((t) => t.state === RewardTierState.Current) ?? null
    },
    /**
     * True when there is at least one applicable tier and every one is unlocked.
     */
    allRewardsUnlocked(): (effectiveSubtotal: number) => boolean {
      return (effectiveSubtotal: number): boolean => {
        const tiers = this.rewardTiers(effectiveSubtotal)
        return tiers.length > 0 && tiers.every((t) => t.state === RewardTierState.Unlocked)
      }
    },
    /**
     * Expanded reward content for a promotion — its Contentful `rewardTracker*` fields resolved into
     * the tracker's shape — or `undefined` when the promotion carries only legacy content. The
     * unlocked label falls back to the base label. Joined by `sunny16Guid === promotionId` via
     * `promotionContent`.
     */
    rewardTrackerContent(): (promotionId: string) => RewardTrackerContent | undefined {
      return (promotionId: string): RewardTrackerContent | undefined => {
        const content = this.promotionContent(promotionId)
        const label = content?.rewardTrackerLabel?.trim()
        if (!label) {
          return undefined
        }
        return {
          label,
          unlockedLabel: content?.rewardTrackerUnlockedLabel?.trim() || label,
          icon: content?.rewardTrackerIcon
        }
      }
    },
    /**
     * Resolved Free Gift callout content for a promotion — its Contentful `freeGiftCallout*` fields — or
     * `undefined` when the promotion has no callout heading authored. A non-empty `freeGiftCalloutHeading`
     * is the content-emergent eligibility signal (mirrors `rewardTrackerContent` keying off its label).
     * Joined by `sunny16Guid === promotionId` via `promotionContent`. The locked item name is resolved
     * elsewhere (granted bundle name / promotion retail name), not here.
     */
    freeGiftContent(): (promotionId: string) => FreeGiftContent | undefined {
      return (promotionId: string): FreeGiftContent | undefined => {
        const content = this.promotionContent(promotionId)
        const heading = content?.freeGiftCalloutHeading?.trim()
        if (!heading) {
          return undefined
        }
        return {
          heading,
          detailImage: content?.freeGiftCalloutDetailImage
        }
      }
    },
    /**
     * ADR 003 first-tier detection: true when the lowest-threshold applicable reward tier carries
     * expanded tracker content — the storefront then renders the multi-tier `RewardsTracker` rather
     * than the legacy `RewardsProgress` bar. Subtotal-independent (keys off content shape, not
     * progress); reads the cached `rewardLadder` (no per-subtotal state stamping needed).
     */
    isExpandedRewardLadder(): boolean {
      const ladder = this.rewardLadder
      return ladder.length > 0 && this.rewardTrackerContent(ladder[0].promotionId) !== undefined
    },
    /**
     * Promotion ids of the tiers unlocked at a given subtotal, in threshold order. The single
     * definition of "which tiers are unlocked" — consumed by both reward crossing detectors
     * (`useRewardCrossingGuard`, `useRewardThresholdCrossing`).
     */
    unlockedRewardTierIds(): (effectiveSubtotal: number) => string[] {
      return (effectiveSubtotal: number): string[] =>
        this.rewardTiers(effectiveSubtotal)
          .filter((tier) => tier.state === RewardTierState.Unlocked)
          .map((tier) => tier.promotionId)
    },
    /**
     * Order-stable signature of the unlocked-tier set at a subtotal (the unlocked ids joined). Compare
     * the signature before vs after a cart mutation to detect a reward-threshold crossing — the shared
     * primitive behind `useRewardCrossingGuard` and `useRewardThresholdCrossing`.
     */
    unlockedRewardTierSignature(): (effectiveSubtotal: number) => string {
      return (effectiveSubtotal: number): string =>
        this.unlockedRewardTierIds(effectiveSubtotal).join(',')
    },
    availablePromotionById(
      state: CatalogStoreState
    ): (promotionId: string) => CatalogPromotionStorefrontDto | null {
      return (promotionId: string) => {
        if (state.catalogData?.availablePromotions) {
          const promotion = state.catalogData.availablePromotions.find(
            (p) => p.promotionId === promotionId
          )

          if (promotion) {
            return promotion
          }
        }

        return null
      }
    },
    /**
     * Active price rules from the subject-aware catalog response. Empty when none apply.
     * Sunny16 bakes the discount into each affected bundle's pricing (`compareAtPrice` / `basePrice`);
     * these entries drive the promotional sprites only.
     */
    activePriceRules(state: CatalogStoreState): ActivePriceRuleStorefrontDto[] {
      return state.catalogData?.activePriceRules ?? []
    },
    /**
     * True when at least one price rule is active — drives the global app-header sprite.
     */
    hasActivePriceRules(): boolean {
      return this.activePriceRules.length > 0
    },
    /**
     * Contentful content for a price rule, joined by `sunny16Guid` <-> `priceRuleId`. Compared
     * case-insensitively for safety (Sunny16 emits a lowercase GUID; Contentful authors uppercase).
     */
    priceRuleContent(): (priceRuleId: string) => ContentfulPriceRuleFlat | undefined {
      return (priceRuleId: string) =>
        this.catalogContent?.priceRules?.find(
          (pr: ContentfulPriceRuleFlat) =>
            pr.sunny16Guid?.toLowerCase() === priceRuleId.toLowerCase()
        )
    },
    /**
     * App-header sprite for the first active rule that has authored content, or null.
     */
    headerPriceRuleSprite(): FlatContentfulEntry<ContentfulPriceRuleSprite> | null {
      for (const rule of this.activePriceRules) {
        const content = this.priceRuleContent(rule.priceRuleId)
        if (content?.appHeaderSprite) {
          return withPriceRuleEndDate(content.appHeaderSprite, rule.calculatedEndDateUtc)
        }
      }
      return null
    },
    /**
     * Mobile explainer-banner sprite for the first active rule that has authored content, or null.
     */
    mobileExplainerSprite(): FlatContentfulEntry<ContentfulPriceRuleSprite> | null {
      for (const rule of this.activePriceRules) {
        const content = this.priceRuleContent(rule.priceRuleId)
        if (content?.mobileExplainerSprite) {
          return withPriceRuleEndDate(content.mobileExplainerSprite, rule.calculatedEndDateUtc)
        }
      }
      return null
    },
    /**
     * Bundle-item sprite for a bundle an active rule discounts, or null. Matches the bundle's
     * `bundleId` against each active rule's `affectedBundleIds` (case-insensitive).
     */
    priceRuleSpriteForBundle(): (
      bundleId: string
    ) => FlatContentfulEntry<ContentfulPriceRuleSprite> | null {
      return (bundleId: string) => {
        const target = bundleId.toLowerCase()
        for (const rule of this.activePriceRules) {
          const matches = rule.affectedBundleIds?.some((id) => id.toLowerCase() === target)
          if (matches) {
            const content = this.priceRuleContent(rule.priceRuleId)
            if (content?.bundleItemSprite) {
              return withPriceRuleEndDate(content.bundleItemSprite, rule.calculatedEndDateUtc)
            }
          }
        }
        return null
      }
    },
    notificationForDisplay(state: CatalogStoreState) {
      return getAvailableNotificationForDisplay(
        state.notificationsContent,
        state.catalogData?.activeCampaigns,
        state.dismissedNotifications
      )
    }
  },
  actions: {
    setCatalogData(data: CatalogStorefrontDto): void {
      this.catalogData = data
    },
    /**
     * Drops the catalog + its Contentful content without touching the rest of the store.
     *
     * The store is persisted, so a load path that legitimately fetches NO catalog (a catalog-less
     * Senior Preorder context, an unmapped preorder catalog) would otherwise keep the previous
     * visit's catalog in scope — stale campaigns/promotions still gating hero selection, stale
     * bundles still priced. `resetStore()` is too broad for that: it also clears
     * `notificationsContent` (set from page content earlier in the same load) and
     * the customer's `activeUpsellsDismissed`.
     */
    clearCatalogData(): void {
      this.catalogData = null
      useCatalogContentStore().resetStore()
    },
    resetStore(): void {
      // This method is used to reset the store to its initial state manually because of the use of persist
      // Any state property added above should be reset here
      this.catalogData = null
      this.dismissedNotifications = []
      this.notificationsContent = null
      this.activeUpsellsDismissed = []
    },
    dismissNotification(notification): void {
      if (!this.dismissedNotifications.includes(notification.sys.id)) {
        this.dismissedNotifications.push(notification.sys.id)
      }
    },
    dismissUpsell(catalogUpsellId: string): void {
      if (!this.activeUpsellsDismissed.includes(catalogUpsellId)) {
        this.activeUpsellsDismissed.push(catalogUpsellId)
      }
    }
  },
  // `catalogContent` moved to `useCatalogContentStore`. Entries written before the move still carry
  // it; omit it on hydrate so `$patch` does not graft a stale 3 MB field back onto this state.
  persist: { omit: ['catalogContent'] }
})
